const attributeKey = 'smartify_governorate';
const validCodes = new Set('BGD KRB BBL DWN NJF BAS AMA SAH ANB NAS KOT SAM KRK SMH ARB DOH MOS DYL HALABJA_UNSUPPORTED'.split(' '));
const selectors = () => [...document.querySelectorAll('smartify-governorate')];
let initialized = false;
let desired = '';
let revision = 0;
let pending = false;
let message = '';
let queue = Promise.resolve();
let checkingOut = false;
let latestComponent;
const approvedForms = new WeakSet();
const lockedLanguageFields = new WeakSet();

async function fetchCart(url, options) {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 15000);
  try {
    const response = await fetch(url, { ...options, signal: controller.signal });
    if (!response.ok) throw new Error('Cart request failed');
    return await response.json();
  } finally {
    clearTimeout(timeout);
  }
}

function sync() {
  const cartView = document.querySelector('.cart-page') || document.querySelector('.cart-drawer__inner');
  if (desired && cartView?.matches('.cart-page--empty, .cart-drawer--empty') && latestComponent) {
    saveSelection(latestComponent, '', true);
    return;
  }
  for (const field of document.querySelectorAll('select[name="language_code"]')) {
    if (pending || checkingOut) {
      if (!field.disabled) {
        lockedLanguageFields.add(field);
        field.disabled = true;
      }
    } else if (lockedLanguageFields.has(field)) {
      field.disabled = false;
      lockedLanguageFields.delete(field);
    }
  }
  for (const component of selectors()) {
    const select = component.querySelector('select');
    if (!select) continue;
    select.value = desired;
    select.setAttribute('aria-invalid', String(message === 'required' || message === 'error'));
    const status = component.querySelector('[data-governorate-status]');
    const key = pending || checkingOut ? (desired ? 'saving' : '') : message || (desired === 'HALABJA_UNSUPPORTED' ? 'manual' : '');
    const statusText = key ? component.dataset[key] || '' : '';
    if (status) {
      const isError = key === 'required' || key === 'error';
      status.dataset.state = key;
      status.setAttribute('role', isError ? 'alert' : 'status');
      status.setAttribute('aria-live', isError ? 'assertive' : 'polite');
    }
    if (status && status.textContent !== statusText) status.textContent = statusText;
    component.setAttribute('aria-busy', String(pending || checkingOut));
    const checkout = component.closest('.cart__ctas')?.querySelector('[name="checkout"]');
    if (checkout) checkout.disabled = pending || checkingOut || component.dataset.cartEmpty === 'true';
  }
}

function saveSelection(component, value, resetting = false) {
  const selection = validCodes.has(value) ? value : '';
  desired = selection;
  const currentRevision = ++revision;
  message = selection || resetting ? '' : 'required';
  pending = true;
  const updateUrl = component.dataset.updateUrl;
  const task = queue.catch(() => {}).then(async () => {
    const cart = await fetchCart(updateUrl, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ attributes: { [attributeKey]: selection } }),
    });
    if ((cart.attributes?.[attributeKey] || '') !== selection) throw new Error('Governorate save mismatch');
    return true;
  }).catch(() => {
    if (currentRevision === revision) message = 'error';
    return false;
  }).finally(() => {
    if (currentRevision === revision) {
      pending = false;
      sync();
    }
  });
  queue = task;
  sync();
  return task;
}

class SmartifyGovernorate extends HTMLElement {
  connectedCallback() {
    const select = this.querySelector('select');
    if (!select) return;
    latestComponent = this;
    if (!initialized) {
      desired = '';
      initialized = true;
      if (this.dataset.savedGovernorate) saveSelection(this, '', true);
    }
    sync();
  }
}

if (!customElements.get('smartify-governorate')) {
  customElements.define('smartify-governorate', SmartifyGovernorate);
}

document.addEventListener('change', (event) => {
  const select = event.target;
  if (!(select instanceof HTMLSelectElement)) return;
  const component = select.closest('smartify-governorate');
  if (component) saveSelection(component, select.value);
});

document.addEventListener('click', (event) => {
  const button = event.target instanceof Element
    ? event.target.closest('button[name="checkout"][form="cart-form"]')
    : null;
  if (!button || button.disabled) return;
  const component = button.closest('.cart__ctas')?.querySelector('smartify-governorate');
  const select = component?.querySelector('select');
  if (!component || !select || validCodes.has(select.value)) return;
  event.preventDefault();
  event.stopImmediatePropagation();
  message = 'required';
  sync();
  select.focus();
}, true);

document.addEventListener('invalid', (event) => {
  if (event.target instanceof HTMLSelectElement && event.target.closest('smartify-governorate')) {
    event.preventDefault();
    message = 'required';
    sync();
    event.target.focus();
  }
}, true);

document.addEventListener('submit', async (event) => {
  const form = event.target;
  if (!(form instanceof HTMLFormElement) || form.id !== 'cart-form') return;
  if (approvedForms.has(form)) {
    approvedForms.delete(form);
    return;
  }
  if (event.submitter && event.submitter.name !== 'checkout') return;
  event.preventDefault();
  event.stopImmediatePropagation();
  if (checkingOut) return;
  const component = event.submitter?.closest('.cart__ctas')?.querySelector('smartify-governorate')
    || selectors().find((item) => item.querySelector('select')?.form === form);
  const select = component?.querySelector('select');
  if (!component || !select || !validCodes.has(select.value)) {
    message = 'required';
    sync();
    select?.reportValidity();
    select?.focus();
    return;
  }
  const checkoutRevision = revision;
  const code = select.value;
  const submitter = event.submitter || document.querySelector('[name="checkout"][form="cart-form"]');
  checkingOut = true;
  sync();
  try {
    await queue;
    if (checkoutRevision !== revision || code !== desired) return;
    if (message === 'error') {
      if (!await saveSelection(component, code)) return;
    }
    const verificationRevision = revision;
    const cart = await fetchCart(component.dataset.cartUrl, { credentials: 'same-origin', cache: 'no-store', headers: { Accept: 'application/json' } });
    if (verificationRevision !== revision || pending) return;
    if (code !== desired || cart.attributes?.[attributeKey] !== code || !cart.item_count) {
      throw new Error('Governorate not confirmed');
    }
    if (!form.isConnected || !select.isConnected || !submitter?.isConnected) return;
    checkingOut = false;
    sync();
    approvedForms.add(form);
    form.requestSubmit(submitter);
    approvedForms.delete(form);
  } catch {
    message = 'error';
  } finally {
    checkingOut = false;
    sync();
  }
}, true);

window.addEventListener('pageshow', (event) => {
  checkingOut = false;
  if (event.persisted && latestComponent) saveSelection(latestComponent, '', true);
  sync();
});

const observer = new MutationObserver(sync);
observer.observe(document.documentElement, { childList: true, subtree: true, attributes: true, attributeFilter: ['selected', 'class', 'data-cart-empty'] });
